import { useState, useEffect, useRef } from 'react';
import { Shield, MoreVertical, Ban, Unlock, Loader2, Plus, Trash2, Languages, Copy } from 'lucide-react';
import Loader from '../../../components/common/Loader/Loader';
import Toast from '../../../components/common/Toast/Toast';
import Modal from '../../../components/common/Modal/Modal';
import { userService } from '../../../services/user.service';
import { useAuth } from '../../../context/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import './UsersPage.css';

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [toast, setToast] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserLangs, setNewUserLangs] = useState([]);
  const [addingUser, setAddingUser] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [loadingAllowed, setLoadingAllowed] = useState(false);
  const [allowedLoaded, setAllowedLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [langTarget, setLangTarget] = useState(null);
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [savingLang, setSavingLang] = useState(false);
  const [copyingTokenFor, setCopyingTokenFor] = useState(null);

  const pageRef = useRef(page);
  const loadingMoreRef = useRef(loadingMore);
  const paginationRef = useRef(pagination);
  const activeTabRef = useRef(activeTab);
  const pageContainerRef = useRef(null);

  const limit = 10;

  // Check if user is admin or super_admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const pendingAllowedCount = allowedUsers.filter((entry) => !entry.hasLoggedIn).length;
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!isAdmin) {
      setError('Access denied - Admin only');
      setLoading(false);
      return;
    }

    fetchUsers(1, false);
    fetchLanguages();
  }, [isAdmin]);

  const fetchLanguages = async () => {
    try {
      const response = await userService.getLanguages();
      setAvailableLanguages(response.data?.langCodes || []);
    } catch (err) {
      console.error('Error fetching languages:', err);
    }
  };

  const fetchAllowedUsers = async () => {
    setLoadingAllowed(true);
    try {
      const response = await userService.getAllowedUsers();
      setAllowedUsers(response.data?.allowedUsers || []);
      setAllowedLoaded(true);
    } catch (err) {
      console.error('Error fetching allowed users:', err);
      setToast({
        message: err.response?.data?.message || 'Failed to load allowed list',
        type: 'error',
      });
    } finally {
      setLoadingAllowed(false);
    }
  };

  // The allowed list is only fetched when its tab is opened for the first time
  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === 'allowed' && !allowedLoaded && !loadingAllowed) {
      fetchAllowedUsers();
    }
  };

  // Keeps an already-loaded list fresh without triggering the first fetch
  const refreshAllowedUsersIfLoaded = () => {
    if (allowedLoaded) {
      fetchAllowedUsers();
    }
  };

  // Infinite scroll: load the next page when the dashboard's scroll
  // container nears the bottom of the users list.
  useEffect(() => {
    if (!isAdmin) return;

    const scrollEl = pageContainerRef.current?.closest('.dashboard-main') || window;

    const handleScroll = () => {
      // The users table is not on screen while the allowed-list tab is open
      if (activeTabRef.current !== 'users') return;
      if (loadingMoreRef.current || !paginationRef.current?.hasNextPage) return;

      const scrollTop = scrollEl === window ? window.scrollY : scrollEl.scrollTop;
      const clientHeight = scrollEl === window ? window.innerHeight : scrollEl.clientHeight;
      const scrollHeight = scrollEl === window ? document.documentElement.scrollHeight : scrollEl.scrollHeight;

      if (scrollTop + clientHeight >= scrollHeight - 150) {
        loadingMoreRef.current = true;
        const nextPage = pageRef.current + 1;
        setPage(nextPage);
        fetchUsers(nextPage, true);
      }
    };

    scrollEl.addEventListener('scroll', handleScroll);
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [isAdmin]);

  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close if click is outside all action menus
      const actionMenus = document.querySelectorAll('.action-dropdown');
      const actionButtons = document.querySelectorAll('.action-menu-button');
      let isClickOutsideMenu = true;

      // Check if click is inside any open action menu
      actionMenus.forEach(menu => {
        if (menu.contains(event.target)) {
          isClickOutsideMenu = false;
        }
      });

      // Check if click is on any action button
      actionButtons.forEach(button => {
        if (button.contains(event.target)) {
          isClickOutsideMenu = false;
        }
      });

      if (isClickOutsideMenu && actionMenuOpen) {
        setActionMenuOpen(null);
      }
    };

    if (actionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen]);

  const fetchUsers = async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await userService.getUsers({
        page: pageNum,
        limit,
      });

      // Backend paginated response: data is the users array directly
      const newUsers = Array.isArray(response.data) ? response.data : [];
      setUsers(prev => (append ? [...prev, ...newUsers] : newUsers));
      setPagination(response.pagination);
      setTotalCount(response.pagination?.total || 0);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleBlock = async (userId, email) => {
    try {
      await userService.blockUser(userId, 'Blocked by admin');
      setToast({ message: `User ${email} has been blocked`, type: 'success' });
      setPage(1);
      fetchUsers(1, false);
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to block user',
        type: 'error',
      });
    }
    setActionMenuOpen(null);
  };

  const handleUnblock = async (userId, email) => {
    try {
      await userService.unblockUser(userId);
      setToast({ message: `User ${email} has been unblocked`, type: 'success' });
      setPage(1);
      fetchUsers(1, false);
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to unblock user',
        type: 'error',
      });
    }
    setActionMenuOpen(null);
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;

    setDeletingUser(true);
    try {
      await userService.deleteUser(deleteTarget._id);
      setToast({ message: `User ${deleteTarget.email} has been deleted`, type: 'success' });
      setDeleteTarget(null);
      setPage(1);
      fetchUsers(1, false);
      // Deleting a user also drops their allowed-list entry
      refreshAllowedUsersIfLoaded();
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to delete user',
        type: 'error',
      });
    } finally {
      setDeletingUser(false);
    }
  };

  const closeAddUser = () => {
    setShowAddUser(false);
    setNewUserEmail('');
    setNewUserLangs([]);
  };

  const toggleNewUserLang = (code) => {
    setNewUserLangs((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    const email = newUserEmail.trim();
    if (!email) return;

    setAddingUser(true);
    try {
      await userService.addAllowedUser(email, newUserLangs);
      const langNote = newUserLangs.length
        ? ` with ${newUserLangs.map((code) => code.toUpperCase()).join(', ')} access`
        : '';
      setToast({
        message: `${email} has been added to the allowed list${langNote}`,
        type: 'success',
      });
      closeAddUser();
      refreshAllowedUsersIfLoaded();
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to add user',
        type: 'error',
      });
    } finally {
      setAddingUser(false);
    }
  };

  // The language modal serves both registered users and allowed-list entries
  const openLangModal = (userItem) => {
    setLangTarget({ kind: 'user', _id: userItem._id, email: userItem.email });
    setSelectedLangs(userItem.lang || []);
    setActionMenuOpen(null);
  };

  const openAllowedLangModal = (entry) => {
    setLangTarget({ kind: 'allowed', _id: entry._id, email: entry.email });
    setSelectedLangs(entry.lang || []);
  };

  const toggleLang = (code) => {
    setSelectedLangs((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSaveLang = async () => {
    if (!langTarget) return;

    setSavingLang(true);
    try {
      if (langTarget.kind === 'allowed') {
        const response = await userService.updateAllowedUserLang(langTarget._id, selectedLangs);
        const updatedLang = response.data?.allowedUser?.lang || selectedLangs;

        setAllowedUsers((prev) =>
          prev.map((entry) =>
            entry._id === langTarget._id ? { ...entry, lang: updatedLang } : entry
          )
        );
        // The backend syncs the user document too, so mirror it in the table
        setUsers((prev) =>
          prev.map((u) => (u.email === langTarget.email ? { ...u, lang: updatedLang } : u))
        );
      } else {
        const response = await userService.updateUserLang(langTarget._id, selectedLangs);
        const updatedLang = response.data?.user?.lang || selectedLangs;

        setUsers((prev) =>
          prev.map((u) => (u._id === langTarget._id ? { ...u, lang: updatedLang } : u))
        );
        setAllowedUsers((prev) =>
          prev.map((entry) =>
            entry.email === langTarget.email ? { ...entry, lang: updatedLang } : entry
          )
        );
      }

      setToast({ message: `Language permissions updated for ${langTarget.email}`, type: 'success' });
      setLangTarget(null);
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to update language permissions',
        type: 'error',
      });
    } finally {
      setSavingLang(false);
    }
  };

  // navigator.clipboard is only available on HTTPS/localhost, so fall back to
  // a hidden textarea + execCommand when the panel is served over plain HTTP.
  const copyToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      const copied = document.execCommand('copy');
      if (!copied) throw new Error('Copy command was rejected');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  // Clicking a user's avatar copies that user's latest active JWT token
  const handleCopyToken = async (userItem) => {
    if (copyingTokenFor) return;

    setCopyingTokenFor(userItem._id);
    try {
      const response = await userService.getUserToken(userItem._id);
      const token = response.data?.token;
      if (!token) {
        throw new Error('No token returned');
      }

      await copyToClipboard(token);
      setToast({ message: `${userItem.email}`, type: 'success' });
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to copy token',
        type: 'error',
      });
    } finally {
      setCopyingTokenFor(null);
    }
  };

  const canBlockUser = (targetRole) => {
    // Super admin can't be blocked
    if (targetRole === 'super_admin') return false;
    // Super admin can block anyone
    if (isSuperAdmin) return true;
    // Admin can block admin and below (but not super admin)
    if (user?.role === 'admin' && targetRole !== 'super_admin') return true;
    return false;
  };

  const canDeleteUser = (targetUserId, targetRole) => {
    // Cannot delete your own account
    if (targetUserId === user?._id) return false;
    // Same permission rules as blocking apply to deleting
    return canBlockUser(targetRole);
  };

  const getRoleBadgeClass = (role) => {
    const roleClasses = {
      super_admin: 'role-badge-super-admin',
      admin: 'role-badge-admin',
      publisher: 'role-badge-publisher',
      editor: 'role-badge-editor',
    };
    return roleClasses[role] || 'role-badge-editor';
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      publisher: 'Publisher',
      editor: 'Editor',
    };
    return roleLabels[role] || role;
  };

  const formatLastActivity = (date) => {
    if (!date) return 'Never';

    const now = new Date();
    const activityDate = new Date(date);
    const diffMs = now - activityDate;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;

    // If less than 24 hours, show relative time
    if (diffHours < 24) {
      if (diffMins < 1) {
        // Less than 1 min - show secs ago
        return `${diffSecs} sec${diffSecs !== 1 ? 's' : ''} ago`;
      } else if (diffHours < 1) {
        // Less than 1 hour - show mins ago
        return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      } else {
        // 1+ hours - show hours and mins
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ${remainingMins} min${remainingMins !== 1 ? 's' : ''} ago`;
      }
    }

    // Otherwise show dd/mm/yy HH:mm format
    const day = String(activityDate.getDate()).padStart(2, '0');
    const month = String(activityDate.getMonth() + 1).padStart(2, '0');
    const year = String(activityDate.getFullYear()).slice(-2);
    const hours = String(activityDate.getHours()).padStart(2, '0');
    const minutes = String(activityDate.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  if (loading && users.length === 0) {
    return (
      <div className="users-page">
        <div className="users-loading">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="users-page" ref={pageContainerRef}>
        {isAdmin && (
          <div className="users-add-section">
            {!showAddUser ? (
              <button
                className="add-user-button"
                onClick={() => setShowAddUser(true)}
              >
                <Plus size={16} />
                Add User
              </button>
            ) : (
              <form className="add-user-form" onSubmit={handleAddUser}>
                <div className="add-user-row">
                  <input
                    type="email"
                    className="add-user-input"
                    placeholder="Enter email address"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    autoFocus
                    required
                  />
                  <button type="submit" className="add-user-submit" disabled={addingUser}>
                    {addingUser ? <Loader2 size={16} className="spin" /> : 'Submit'}
                  </button>
                  <button
                    type="button"
                    className="add-user-cancel"
                    onClick={closeAddUser}
                    disabled={addingUser}
                  >
                    Cancel
                  </button>
                </div>

                {/* Languages assigned here are applied to the user when they first log in */}
                <div className="add-user-lang">
                  <span className="add-user-lang-label">
                    <Languages size={14} />
                    Languages
                    <span className="add-user-lang-hint">(optional, can be changed later)</span>
                  </span>
                  <div className="add-user-lang-options">
                    {availableLanguages.length === 0 ? (
                      <span className="add-user-lang-empty">No languages available</span>
                    ) : (
                      availableLanguages.map((language) => {
                        const isSelected = newUserLangs.includes(language.code);
                        return (
                          <button
                            key={language.code}
                            type="button"
                            className={`add-user-lang-chip ${isSelected ? 'add-user-lang-chip--active' : ''}`}
                            onClick={() => toggleNewUserLang(language.code)}
                            disabled={addingUser}
                            title={language.name}
                          >
                            {language.code.toUpperCase()}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Tabs: registered users and the allowed list */}
        {isAdmin && (
          <div className="users-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'users'}
              className={`users-tab ${activeTab === 'users' ? 'users-tab--active' : ''}`}
              onClick={() => handleTabChange('users')}
            >
              Users
              {totalCount > 0 && <span className="users-tab-count">{totalCount}</span>}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'allowed'}
              className={`users-tab ${activeTab === 'allowed' ? 'users-tab--active' : ''}`}
              onClick={() => handleTabChange('allowed')}
            >
              Allowed Users
              {loadingAllowed && <Loader2 size={13} className="spin users-tab-spinner" />}
              {allowedLoaded && <span className="users-tab-count">{allowedUsers.length}</span>}
              {allowedLoaded && pendingAllowedCount > 0 && (
                <span className="users-tab-pending">{pendingAllowedCount} pending</span>
              )}
            </button>
          </div>
        )}

        {/* Allowed list - includes emails that have not logged in yet */}
        {isAdmin && activeTab === 'allowed' && (
          <div className="allowed-section">
            <div className="allowed-list">
              {loadingAllowed && allowedUsers.length === 0 ? (
                <p className="allowed-list-empty">Loading allowed list...</p>
              ) : !allowedLoaded ? (
                <button
                  type="button"
                  className="allowed-list-retry"
                  onClick={fetchAllowedUsers}
                >
                  Could not load the allowed list. Retry
                </button>
              ) : allowedUsers.length === 0 ? (
                <p className="allowed-list-empty">
                  No emails on the allowed list yet. Use Add User to add one.
                </p>
              ) : (
                allowedUsers.map((entry) => (
                  <div key={entry._id} className="allowed-row">
                    <div className="allowed-row-main">
                      <span className="allowed-row-email">{entry.email}</span>
                      <span
                        className={`allowed-row-status ${entry.hasLoggedIn ? 'allowed-row-status--joined' : 'allowed-row-status--pending'}`}
                      >
                        {entry.hasLoggedIn ? 'Logged in' : 'Not logged in'}
                      </span>
                    </div>

                    <div className="allowed-row-langs">
                      {entry.lang?.length > 0 ? (
                        entry.lang.map((code) => (
                          <span key={code} className="allowed-row-lang-chip">
                            {code.toUpperCase()}
                          </span>
                        ))
                      ) : (
                        <span className="allowed-row-no-lang">No languages</span>
                      )}
                    </div>

                    <button
                      type="button"
                      className="allowed-row-edit"
                      onClick={() => openAllowedLangModal(entry)}
                      title={`Edit languages for ${entry.email}`}
                    >
                      <Languages size={14} />
                      Edit
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Last Activity</th>
                {users.some(u => u.status) && <th>Status</th>}
                {users.some(u => u.role) && <th>Role</th>}
                {isAdmin && <th>Languages</th>}
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((userItem) => (
                <tr key={userItem._id} className="user-row">
                  <td className="user-info-cell">
                    <div className="user-avatar-wrapper">
                      <button
                        type="button"
                        className="user-avatar-button"
                        onClick={() => handleCopyToken(userItem)}
                        disabled={copyingTokenFor !== null}
                        title={`Copy latest JWT token for ${userItem.email}`}
                      >
                        {userItem.profilePicture ? (
                          <img
                            src={userItem.profilePicture}
                            alt=""
                            className="user-avatar"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className="user-avatar-placeholder"
                          style={{ display: userItem.profilePicture ? 'none' : 'flex' }}
                        >
                          {(userItem.firstName || userItem.email || '').charAt(0).toUpperCase()}
                        </div>
                        <span className="user-avatar-copy-overlay">
                          {copyingTokenFor === userItem._id ? (
                            <Loader2 size={16} className="spin" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </span>
                      </button>
                      <div className="user-details">
                        <div className="user-name">
                          {userItem.firstName || userItem.lastName
                            ? `${userItem.firstName || ''} ${userItem.lastName || ''}`.trim()
                            : 'User'}
                          {userItem.metadata?.isDevUser && (
                            <span className="dev-badge">Dev</span>
                          )}
                        </div>
                        <div className="user-email-text">{userItem.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="last-activity">
                    {formatLastActivity(userItem.lastActivity)}
                  </td>
                  {userItem.status !== undefined && (
                    <td>
                      <span className={`status-badge ${userItem.status === 'blocked' ? 'status-blocked' : 'status-active'}`}>
                        {userItem.status === 'blocked' ? 'Blocked' : 'Active'}
                      </span>
                    </td>
                  )}
                  {userItem.role !== undefined && (
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(userItem.role)}`}>
                        {getRoleLabel(userItem.role)}
                      </span>
                    </td>
                  )}
                  {isAdmin && (
                    <td className="lang-cell">
                      <div className="lang-chips">
                        {(userItem.lang || []).length > 0 ? (
                          userItem.lang.map((code) => (
                            <span key={code} className="lang-chip">{code.toUpperCase()}</span>
                          ))
                        ) : (
                          <span className="lang-chip-all">All Languages</span>
                        )}
                        <button
                          type="button"
                          className="lang-edit-button"
                          onClick={() => openLangModal(userItem)}
                          title="Edit language permissions"
                        >
                          <Languages size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                  {isAdmin && (
                    <td className="actions-cell">
                      <div className="action-menu">
                        <button
                          onClick={() => setActionMenuOpen(actionMenuOpen === userItem._id ? null : userItem._id)}
                          className="action-menu-button"
                        >
                          <MoreVertical size={18} />
                        </button>

                        {actionMenuOpen === userItem._id && (
                          <div className="action-dropdown">
                            {userItem.status === 'active' ? (
                              <button
                                onClick={() => handleBlock(userItem._id, userItem.email)}
                                disabled={!canBlockUser(userItem.role)}
                                className={`action-item ${!canBlockUser(userItem.role) ? 'action-item-disabled' : 'action-item-danger'}`}
                              >
                                <Ban size={16} />
                                Block User
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUnblock(userItem._id, userItem.email)}
                                className="action-item action-item-success"
                              >
                                <Unlock size={16} />
                                Unblock User
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setDeleteTarget(userItem);
                                setActionMenuOpen(null);
                              }}
                              disabled={!canDeleteUser(userItem._id, userItem.role)}
                              className={`action-item ${!canDeleteUser(userItem._id, userItem.role) ? 'action-item-disabled' : 'action-item-danger'}`}
                            >
                              <Trash2 size={16} />
                              Delete User
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {/* Extra empty row for spacing at the bottom */}
              <tr className="user-row empty-row">
                <td
                  colSpan={
                    2 +
                    (users.some(u => u.status) ? 1 : 0) +
                    (users.some(u => u.role) ? 1 : 0) +
                    (isAdmin ? 2 : 0)
                  }
                  style={{ height: '60px' }}
                ></td>
              </tr>
            </tbody>
          </table>

          {!loading && users.length === 0 && (
            <div className="users-empty">
              <Shield size={48} />
              <p>No users found</p>
            </div>
          )}
        </div>
        )}

        {activeTab === 'users' && (
          <div className="users-pagination">
            {loadingMore && (
              <span className="pagination-loading-more">
                <Loader2 size={16} className="spin" />
                Loading more...
              </span>
            )}
            <span className="pagination-count">
              Total Users: {totalCount}
            </span>
          </div>
        )}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => !deletingUser && setDeleteTarget(null)}
        title="Delete User"
        size="small"
      >
        <p>
          Are you sure you want to delete <strong>{deleteTarget?.email}</strong>?
          This will permanently remove the user, all their sessions, and their
          entry from the allowed users list. This action cannot be undone.
        </p>
        <div className="delete-user-modal-actions">
          <button
            type="button"
            className="add-user-cancel"
            onClick={() => setDeleteTarget(null)}
            disabled={deletingUser}
          >
            Cancel
          </button>
          <button
            type="button"
            className="delete-user-confirm"
            onClick={handleDeleteUser}
            disabled={deletingUser}
          >
            {deletingUser ? <Loader2 size={16} className="spin" /> : 'Delete'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={!!langTarget}
        onClose={() => !savingLang && setLangTarget(null)}
        title="Language Permissions"
        size="small"
        className="lang-modal"
      >
        <p className="lang-modal-subtitle">
          Languages <strong>{langTarget?.email}</strong> can edit news in.
          {langTarget?.kind === 'allowed' && !allowedUsers.find((entry) => entry._id === langTarget._id)?.hasLoggedIn && (
            <span className="lang-modal-note"> Applied as soon as they log in for the first time.</span>
          )}
        </p>
        <div className="lang-modal-options">
          {availableLanguages.length === 0 && (
            <p className="lang-modal-empty">No languages available.</p>
          )}
          {availableLanguages.map((language) => {
            const isSelected = selectedLangs.includes(language.code);
            return (
              <label key={language.code} className="lang-modal-option">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleLang(language.code)}
                />
                <span className="lang-modal-option-code">{language.code.toUpperCase()}</span>
                <span className="lang-modal-option-name">{language.name}</span>
              </label>
            );
          })}
        </div>
        <div className="delete-user-modal-actions">
          <button
            type="button"
            className="add-user-cancel"
            onClick={() => setLangTarget(null)}
            disabled={savingLang}
          >
            Cancel
          </button>
          <button
            type="button"
            className="add-user-submit"
            onClick={handleSaveLang}
            disabled={savingLang}
          >
            {savingLang ? <Loader2 size={16} className="spin" /> : 'Save'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default UsersPage;
